import { parseQueryString } from './query-string';

/**
 * User agents of crawlers and headless browsers that should not create Datadog RUM sessions.
 * Includes Bingbot (whose UA is often malformed), HeadlessChrome (e.g. pdf-service screenshots),
 * ActiveComply compliance scans, the Reflectionbot AI training crawler, GoogleOther, Meta's crawlers
 * (named explicitly rather than relying on their info URL containing "crawler"), and the CURateSurvey
 * and RootEvidence scrapers seen in RUM.
 */
export const BOT_USER_AGENT = new RegExp(
  [
    'bot',
    'crawl',
    'spider',
    'slurp',
    'headlesschrome',
    'phantomjs',
    'bingbot',
    'activecomply',
    'reflectionbot',
    'googleother',
    'meta-externalagent',
    'meta-webindexer',
    'facebookexternalhit',
    'curatesurvey',
    'rootevidence',
  ].join('|'),
  'i',
);

/**
 * Query parameter that forces RUM recording on, bypassing bot detection.
 * Intended for synthetic monitors and headless tests that should appear in RUM, e.g. `?ddRum=1`.
 */
export const RUM_OVERRIDE_PARAM = 'ddRum';

/**
 * Heuristically detects crawlers, headless browsers and automation-driven sessions.
 *
 * This is a filter, not a guarantee: stealth tooling can spoof the user agent and hide `navigator.webdriver`.
 *
 * @returns {boolean} `true` if the user agent matches a known bot, the browser is driven by WebDriver,
 * or the window has zero size.
 */
export const looksAutomated = (): boolean =>
  BOT_USER_AGENT.test(navigator.userAgent) ||
  navigator.webdriver === true ||
  (window.outerWidth === 0 && window.outerHeight === 0);

/**
 * Query string flag indicating whether the page URL explicitly opts in to RUM recording via `?ddRum=1`.
 *
 * @returns {boolean} `true` if the override query parameter is set to `1`.
 */
export const isRumForced = (): boolean => parseQueryString([RUM_OVERRIDE_PARAM])?.[RUM_OVERRIDE_PARAM] === '1';

/**
 * Determines the Datadog RUM session sample rate for the current client.
 * Automated clients get 0 so their sessions are never created or billed, unless forced on via the override.
 *
 * @returns {number} The session sample rate as a percentage (0 or 100).
 */
export const getRumSessionSampleRate = (): number => (isRumForced() || !looksAutomated() ? 100 : 0);
